import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Store } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { CategoryRating, OverallRating } from "../components/StarRating";

const EMPTY = { overall: 0, ratings: {}, comment: "", customer_name: "", customer_email: "" };

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#EEF1ED] flex flex-col items-center justify-center p-4 sm:p-6">
    {children}
    <p className="mt-6 text-xs text-[#778580]">Powered by <span className="font-semibold text-[#4B5A54]">Bonus Thought</span></p>
  </div>
);

const Notice = ({ title, body, testId }) => (
  <Shell>
    <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-xl border border-slate-200/80 text-center" data-testid={testId}>
      <Store className="w-10 h-10 mx-auto text-[#778580]" />
      <h1 className="mt-4 font-display text-2xl font-bold">{title}</h1>
      <p className="mt-2 text-[#4B5A54]">{body}</p>
    </div>
  </Shell>
);

export default function Survey() {
  const { slug } = useParams();
  const [biz, setBiz] = useState(undefined);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/businesses/${slug}`).then((r) => setBiz(r.data)).catch(() => setBiz(null));
  }, [slug]);

  if (biz === undefined) return <Shell><Loader2 className="w-7 h-7 animate-spin text-[#2F5D4A]" /></Shell>;
  if (biz === null) return <Notice testId="survey-not-found" title="Survey not found" body="This link doesn't match any business. Please check the QR code and try again." />;
  if (!biz.active) return <Notice testId="survey-inactive" title={biz.name} body="This survey isn't accepting responses right now. Thanks for stopping by!" />;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const rate = (key) => (v) => setForm((f) => ({ ...f, ratings: { ...f.ratings, [key]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.overall) return toast.error("Please choose an overall star rating");
    setSending(true);
    try {
      await api.post(`/public/businesses/${slug}/feedback`, {
        overall: form.overall,
        ratings: form.ratings,
        comment: form.comment,
        customer_name: form.customer_name.trim() || null,
        customer_email: form.customer_email.trim() || null,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  if (done)
    return (
      <Shell>
        <div className="w-full max-w-md bg-white rounded-3xl p-8 sm:p-10 shadow-xl border border-slate-200/80 text-center pop-in" data-testid="survey-thank-you-card">
          <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-[#E3EEF3] text-[#2E6F8E]"><CheckCircle2 className="w-9 h-9" /></span>
          <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tight">Thank you!</h1>
          <p className="mt-2 text-[#4B5A54]">Your feedback helps <strong className="text-[#1A2B25]">{biz.name}</strong> get better every day.</p>
          <button onClick={() => { setForm(EMPTY); setDone(false); }} className="btn-secondary mt-8 mx-auto" data-testid="survey-submit-another">Leave another response</button>
        </div>
      </Shell>
    );

  return (
    <Shell>
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-slate-200/80 space-y-7 fade-up" data-testid="survey-form">
        <div>
          <p className="label-caps" data-testid="survey-business-category">{[biz.category || "Customer survey", biz.location].filter(Boolean).join(" · ")}</p>
          <h1 className="mt-1 font-display text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="survey-business-name">How was your visit to {biz.name}?</h1>
          <p className="mt-1.5 text-sm text-[#4B5A54]">Takes about 30 seconds. Your honest feedback means a lot.</p>
        </div>
        <div>
          <p className="text-sm font-semibold mb-3">Overall satisfaction <span className="text-[#2F5D4A]">*</span></p>
          <OverallRating value={form.overall} onChange={set("overall")} />
        </div>
        <div className="rounded-2xl bg-[#EEF1ED] border border-slate-200/80 px-4 py-2 divide-y divide-slate-200/70">
          {biz.categories.map((c) => (
            <CategoryRating key={c.key} label={c.label} value={form.ratings[c.key] || 0} onChange={rate(c.key)} testId={`survey-${c.key}-rating`} />
          ))}
        </div>
        <label className="block">
          <span className="text-sm font-semibold">Tell us more <span className="font-normal text-[#778580]">(optional)</span></span>
          <textarea rows={4} maxLength={2000} value={form.comment} onChange={(e) => set("comment")(e.target.value)} data-testid="survey-comment-input"
            placeholder="What did you love? What could be better?" className="input-base mt-1.5 resize-none" />
        </label>
        <div>
          <p className="text-sm font-semibold">Want a follow-up? <span className="font-normal text-[#778580]">(optional)</span></p>
          <div className="mt-1.5 grid sm:grid-cols-2 gap-2">
            <input value={form.customer_name} onChange={(e) => set("customer_name")(e.target.value)} placeholder="Your name" maxLength={80} className="input-base" data-testid="survey-name-input" />
            <input type="email" value={form.customer_email} onChange={(e) => set("customer_email")(e.target.value)} placeholder="Email" className="input-base" data-testid="survey-email-input" />
          </div>
        </div>
        <button type="submit" disabled={sending} className="btn-primary w-full justify-center !py-3.5 text-base" data-testid="survey-submit-button">
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send feedback"}
        </button>
      </form>
    </Shell>
  );
}
